import { restApiClientRes } from '../../../api/rest-api-client'
import { encodeNestedObject } from '../functions/misc.functions'
import { getFilenameFromContentDisposition } from './get-file-name-from-header'

export type DownloadDocumentsOptions = {
  automaticallyDownload?: boolean
}

export type DownloadDocumentsResult = {
  downloadFn: () => void
  fileLink?: string
  filename?: string
  mimeType?: string
  cleanup: () => void
}

export type DownloadDocumentsInput<T extends object | undefined = undefined> = {
  endpoint: string
  ids?: number[]
} & (T extends undefined ? { data?: never } : { data: T })

export async function downloadDocuments<T extends object | undefined = undefined>(
  input: DownloadDocumentsInput<T>,
  options: DownloadDocumentsOptions = { automaticallyDownload: true }
): Promise<DownloadDocumentsResult> {
  const { endpoint, ids, data } = input as DownloadDocumentsInput<object>

  if (ids && !Array.isArray(ids)) throw new Error('ids must be an array')

  const req = restApiClientRes.url(encodeNestedObject(endpoint, { ids, ...data }))
  const res = await req.get().res()

  if (!res.ok) throw new Error(`Download failed: ${res.status}`)

  const mimeType = res.headers.get('content-type') ?? undefined
  const filename =
    getFilenameFromContentDisposition(res.headers.get('content-disposition')) ?? undefined

  const blob = await res.blob()
  const blobUrl = URL.createObjectURL(blob)

  const downloadFn = () => {
    const a = document.createElement('a')
    a.href = blobUrl
    if (filename) a.download = filename // backend-controlled
    document.body.appendChild(a)
    a.click()
    document.body.removeChild(a)
  }

  const cleanup = () => URL.revokeObjectURL(blobUrl)

  if (options.automaticallyDownload) downloadFn()

  return { downloadFn, fileLink: blobUrl, filename, mimeType, cleanup }
}
